import type { PublicLocation } from '../types';

/** Convert hex color to rgba string. */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6 && clean.length !== 3) return hex;
  const full =
    clean.length === 3
      ? clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2]
      : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Format a time string like "09:00" to "9:00 AM". */
export function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

export const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export function formatLocationAddressLines(loc: PublicLocation): string[] {
  const lines: string[] = [];
  if (loc.address_primary) {
    const a = [loc.address_primary, loc.address_secondary].filter(Boolean).join(', ');
    lines.push(a);
  }
  const city = [loc.address_city, loc.address_state].filter(Boolean).join(', ');
  const cityZip = [city, loc.address_zip_code].filter(Boolean).join(' ').trim();
  if (cityZip) lines.push(cityZip);
  if (loc.address_country) lines.push(loc.address_country);
  return lines;
}

export function locationEmbedQuery(loc: PublicLocation): string | null {
  if (loc.google_place_id?.trim()) {
    return `place_id:${loc.google_place_id.trim()}`;
  }
  const parts = [
    loc.address_primary,
    loc.address_secondary,
    loc.address_city,
    loc.address_state,
    loc.address_zip_code,
    loc.address_country,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(', ');
}

export function reviewerAvatarLabel(review: { reviewer_initials?: string; reviewer_name?: string }): string {
  if (review.reviewer_initials?.trim()) return review.reviewer_initials.trim().slice(0, 3).toUpperCase();
  const name = review.reviewer_name?.trim() || '';
  const bits = name.split(/\s+/).filter(Boolean);
  if (bits.length >= 2) {
    return (bits[0][0] + bits[bits.length - 1][0]).toUpperCase();
  }
  return name.charAt(0).toUpperCase() || '?';
}
